import React from "react";
import { Lock, ExternalLink } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { fmtEUR } from "@/pages/cotizador/shared";

const fmtUSDNat = (v) => {
  if (v == null || Number.isNaN(v)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(v);
};

const Row = ({ label, value, testid, hint, strong = false, muted = false }) => (
  <div className={`flex items-baseline justify-between ${strong ? "text-base font-bold" : "text-sm"} ${muted ? "text-muted-foreground" : ""}`}>
    <span className="flex items-center gap-1.5">{label}{hint ? <span className="text-[10px] text-muted-foreground">{hint}</span> : null}</span>
    <span className="tabular-nums" data-testid={testid}>{value}</span>
  </div>
);

/**
 * Replica exacta del "Pricing Summary" que ve el specialist en KimKim.
 * Todo bloqueado: los valores vienen del scrape del /tp/{code}/markup-itemized-pricing
 * y no son editables desde SOFI. Para cambiarlos hay que editar el trip plan en
 * KimKim y volver a importar.
 */
export function CotKimKimPricingSummary({ doc }) {
  const p = doc?.kimkim_pricing_summary || {};
  const hasData = p && (p.trip_total_eur || p.subtotal_eur);
  const kkUrl = doc?.import_source_url;

  return (
    <Card className="border-primary/30 bg-primary/[0.03]" data-testid="cot-kimkim-pricing-summary">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Lock className="h-4 w-4 text-primary" />
            Pricing Summary
          </CardTitle>
          <Badge variant="outline" className="ml-auto text-[10px] uppercase tracking-wide">
            KimKim · bloqueado
          </Badge>
        </div>
        {p.exchange_rate ? (
          <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>Exchange rate</span>
            <span className="tabular-nums" data-testid="kk-summary-fx">1 EUR = ${p.exchange_rate}</span>
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {!hasData ? (
          <p className="text-sm text-muted-foreground">
            No hay Pricing Summary scrapeado. Reimporta el viaje para traer las cifras KimKim.
          </p>
        ) : (
          <>
            {/* Itemized Pricing block (fondo gris como en KimKim) */}
            <div className="space-y-2 rounded-lg bg-muted/50 p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Itemized Pricing</div>
              <Row label="Activities & Transportation" value={`${fmtEUR(p.activities_transportation_eur)} EUR`} testid="kk-summary-activities" />
              <Row label="Accommodations" value={`${fmtEUR(p.accommodations_eur)} EUR`} testid="kk-summary-accommodations" />
              <div className="flex items-baseline justify-between text-sm">
                <span className="flex items-center gap-2">
                  Markup <span className="inline-flex h-6 min-w-[3rem] items-center justify-center rounded border bg-background px-2 text-xs font-semibold tabular-nums" data-testid="kk-summary-markup-pct">{p.markup_pct ?? 0}</span>
                  <span className="text-xs text-muted-foreground">%</span>
                </span>
                <span className="tabular-nums" data-testid="kk-summary-markup-eur">{fmtEUR(p.markup_eur)} EUR</span>
              </div>
              <div className="mt-2 border-t border-border/60 pt-2">
                <Row label="Subtotal" value={`${fmtEUR(p.subtotal_eur)} EUR`} testid="kk-summary-subtotal" strong />
              </div>
            </div>

            {/* Comisión + fees KimKim (fuera del itemized) */}
            <div className="space-y-2">
              <Row
                label={`Kimkim commission (${(p.kimkim_commission_pct ?? 0).toFixed(1)}%)`}
                value={`${fmtEUR(p.kimkim_commission_eur)} EUR`}
                testid="kk-summary-commission"
              />
              <Row label="Service Fee" value={`${fmtUSDNat(p.service_fee_usd)} USD`} testid="kk-summary-servicefee" />
              <Row label="Carbon removal" value={`${fmtUSDNat(p.carbon_removal_usd)} USD`} testid="kk-summary-carbon" />
            </div>

            {/* Trip total destacado */}
            <div className="flex items-center justify-between border-t pt-3">
              <span className="text-base font-bold">Trip total</span>
              <span className="text-xl font-bold tabular-nums" data-testid="kk-summary-trip-total">
                {fmtEUR(p.trip_total_eur)} EUR
              </span>
            </div>

            {/* Aviso: bloqueado */}
            <div className="rounded-md border border-primary/20 bg-primary/[0.05] p-2.5 text-[11px] leading-relaxed text-muted-foreground">
              <div className="mb-0.5 flex items-center gap-1 font-semibold text-foreground">
                <Lock className="h-3 w-3" /> Cifras bloqueadas
              </div>
              Estos importes vienen directamente del Pricing Summary de KimKim y no son editables desde SOFI. Para modificarlos, edita el trip plan en KimKim y reimporta.
              {kkUrl ? (
                <a
                  href={kkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="kk-summary-open-original"
                  className="mt-1.5 inline-flex items-center gap-1 font-medium text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" /> Abrir en KimKim
                </a>
              ) : null}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
